/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Error Boundary Component
 *
 * Core error boundary class component extracted from error-boundary-enhanced for modularity.
 */

"use client";

import * as Sentry from "@sentry/nextjs";
import React from "react";
import {
  DefaultErrorFallback,
  type ErrorFallbackProps,
} from "./components/default-error-fallback";

export interface ErrorInfo {
  componentStack: string;
  errorBoundary?: string;
  errorMessage?: string;
}

export interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  eventId?: string;
}

export interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<ErrorFallbackProps> | React.ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showDetails?: boolean;
  isolationKey?: string;
  resetOnPropsChange?: boolean;
  resetKeys?: Array<string | number>;
}

/**
 * Error Boundary Class Component
 *
 * Catches React errors and displays fallback UI.
 */
export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  private resetTimeoutId: number | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  override componentDidUpdate(prevProps: ErrorBoundaryProps) {
    const { resetKeys, resetOnPropsChange } = this.props;
    const { hasError } = this.state;

    if (hasError && prevProps.resetKeys !== resetKeys) {
      if (resetKeys?.some((key, idx) => prevProps.resetKeys?.[idx] !== key)) {
        this.resetError();
      }
    }

    if (
      hasError &&
      resetOnPropsChange &&
      prevProps.children !== this.props.children
    ) {
      this.resetError();
    }
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const info: ErrorInfo = {
      componentStack: errorInfo.componentStack ?? "",
      errorBoundary: this.props.isolationKey ?? "UnknownBoundary",
      errorMessage: error.message ?? "",
    };

    // Report to Sentry
    const eventId = Sentry.captureException(error, {
      contexts: {
        react: { componentStack: info.componentStack },
      },
      tags: {
        errorBoundary: info.errorBoundary,
      },
    });

    this.setState({
      errorInfo: info,
      eventId,
    });

    // Call custom error handler
    this.props.onError?.(error, info);
  }

  resetError = () => {
    if (this.resetTimeoutId) {
      window.clearTimeout(this.resetTimeoutId);
    }

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: undefined,
    });
  };

  override render() {
    const { hasError, error, errorInfo, eventId } = this.state;
    const { children, fallback: Fallback, showDetails = false } = this.props;

    if (hasError && error) {
      if (Fallback) {
        if (typeof Fallback === "function") {
          return (
            <Fallback
              error={error}
              errorInfo={errorInfo}
              resetError={this.resetError}
              eventId={eventId}
              showDetails={showDetails}
            />
          );
        }
        // ReactNode fallback (plain JSX)
        return Fallback as React.ReactNode;
      }

      return (
        <DefaultErrorFallback
          error={error}
          errorInfo={errorInfo}
          resetError={this.resetError}
          eventId={eventId}
          showDetails={showDetails}
        />
      );
    }

    return children;
  }
}
